import { Box } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { QtyBlip } from "@smartrr/shared/customerPortal/components/QtyBlip";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import {
  getPurchasableFromPurchasableMap,
  getPurchasableImage,
  getPurchasableSmallImage,
  getVariantImageFromPurchasableMap,
  getVariantSmallImageFromPurchasableMap,
} from "@smartrr/shared/utils/useVariantToPurchasableMap";
import cn from "classnames";
import React, { ReactEventHandler, useCallback, useState } from "react";
import styled from "styled-components";

import { useCustomerPortalVariantToPurchasableMap } from "@customer-app/state/reducers/purchasables";
import { classNamePrefixer } from "@customer-app/utils/classNamePrefixer";

export const WIDTH_SIZES = {
  small: 44,
  medium: 90,
  large: 160,
  largest: 400,
};

const StyledImageWrapper = styled.div.attrs(classNamePrefixer("product-image-wrapper"))<{
  size: Props["size"];
}>`
  border-radius: ${props => {
    switch (props.size) {
      case "small": {
        return props.theme.smallProductImage?.borderRadius;
      }
      case "medium": {
        return props.theme.mediumProductImage?.borderRadius;
      }
      case "large": {
        return props.theme.largeProductImage?.borderRadius;
      }
      case "largest": {
        return props.theme.largestProductImage?.borderRadius;
      }
      default: {
        return 0;
      }
    }
  }};
`;

const StyledImageContainer = styled.div.attrs(classNamePrefixer("product-image-container"))<{
  size: Props["size"];
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => WIDTH_SIZES[props.size || "medium"]}px;
  height: ${props => WIDTH_SIZES[props.size || "medium"]}px;
  min-width: ${props => WIDTH_SIZES[props.size || "medium"]}px;
  min-height: ${props => WIDTH_SIZES[props.size || "medium"]}px;
  background-color: inherit;
  overflow: hidden;
  border-radius: ${props => {
    switch (props.size) {
      case "small": {
        return props.theme.smallProductImage?.borderRadius;
      }
      case "medium": {
        return props.theme.mediumProductImage?.borderRadius;
      }
      case "large": {
        return props.theme.largeProductImage?.borderRadius;
      }
      default: {
        return 0;
      }
    }
  }};

  &.error {
    position: relative;
    &:after {
      border: 1px solid ${props => props.theme.accentText?.color};
      background: "#FFFFFF";
      content: "";
      height: 100%;
      width: 100%;
      top: 0;
      position: absolute;
      left: 0;
      display: block;
    }
  }
`;

type ImageDirection = "vertical" | "horizontal";
const StyledImage = styled.img.attrs<{
  direction: ImageDirection;
}>(props => ({
  ...props,
  className: cn(prefixClassName("product-image"), prefixClassName(`product-image-${props.direction}`)),
}))<{
  size: Props["size"];
  isLoaded: boolean;
  direction: ImageDirection;
  disabled: boolean;
}>`
  max-height: 100%;
  max-width: 100%;
  transition: opacity 0.15s ease-in-out;
  ${props =>
    props.disabled &&
    `
    opacity: 50%;
  `}
  ${props =>
    props.isLoaded
      ? `
    opacity: 1;
  `
      : `
    visibility: hidden;
    opacity: 0;
  `}
  ${props =>
    props.direction === "horizontal"
      ? `
        min-height: 0;
        min-width: ${WIDTH_SIZES[props.size || "medium"]}px;
      `
      : `
        min-height: ${WIDTH_SIZES[props.size || "medium"]}px;
        min-width: 0;
      `}
`;

const FallbackImage = styled.div.attrs(props => ({
  ...props,
  className: prefixClassName("product-fallback-image"),
}))<{
  size: Props["size"];
}>`
  border: 1px solid ${props => props.theme.accentText?.color};
  width: ${props => WIDTH_SIZES[props.size || "medium"]}px;
  height: ${props => WIDTH_SIZES[props.size || "medium"]}px;
  border-radius: ${props => {
    switch (props.size) {
      case "small": {
        return props.theme.smallProductImage?.borderRadius;
      }
      case "medium": {
        return props.theme.mediumProductImage?.borderRadius;
      }
      case "large": {
        return props.theme.largeProductImage?.borderRadius;
      }
      default: {
        return 0;
      }
    }
  }};
  &:empty {
    display: block;
  }
`;

interface Props {
  purchasable?: IPurchasable;
  purchasableVariant?: IPurchasableVariant;
  className?: string;
  onClick?(e: React.SyntheticEvent<HTMLDivElement, Event>): void;
  size?: keyof typeof WIDTH_SIZES;
  quantity?: number;
  disabled?: boolean;
  lazyLoading?: boolean;
}

export function ProductImage({
  className,
  purchasable,
  purchasableVariant,
  size = "medium",
  quantity,
  disabled,
  lazyLoading = false,
  ...other
}: Props): JSX.Element {
  const [productImageHadError, setProductImageHadError] = useState(false);
  const [productVariantImageHadError, setProductVariantImageHadError] = useState(false);
  const [isLoaded, setIsLoadedState] = useState(false);
  const [direction, setDirection] = useState<ImageDirection>("horizontal");
  const variantToPurchasableMap = useCustomerPortalVariantToPurchasableMap();
  const resolvedPurchasable =
    purchasable || getPurchasableFromPurchasableMap(variantToPurchasableMap, purchasableVariant);

  let image: string | undefined;
  if (size === "large" || size === "largest") {
    image =
      purchasableVariant && !productVariantImageHadError
        ? getVariantImageFromPurchasableMap(variantToPurchasableMap, purchasableVariant)
        : getPurchasableImage(resolvedPurchasable);
  } else {
    image =
      purchasableVariant && !productVariantImageHadError
        ? getVariantSmallImageFromPurchasableMap(variantToPurchasableMap, purchasableVariant)
        : getPurchasableSmallImage(resolvedPurchasable);
  }

  const setLoadedCb: ReactEventHandler<HTMLImageElement> = useCallback(e => {
    const heightToWidth = e.currentTarget.naturalHeight / e.currentTarget.naturalWidth;
    if (heightToWidth > 1) {
      setDirection("vertical");
    }
    setIsLoadedState(true);
  }, []);
  const setHadErrorCb = useCallback(() => {
    if (purchasableVariant && !productVariantImageHadError) {
      setProductVariantImageHadError(true);
    } else {
      setProductImageHadError(true);
    }
  }, []);

  return (
    <StyledImageWrapper
      size={size}
      className={cn(className, {
        error: productImageHadError,
        size,
      })}
    >
      <Box sx={{ position: "relative" }}>
        <StyledImageContainer
          size={size}
          className={cn(className, size, {
            error: productImageHadError,
          })}
          {...other}
        >
          {image ? (
            <StyledImage
              src={image}
              size={size}
              direction={direction}
              alt={`${purchasable?.purchasableName ? `Add ${purchasable.purchasableName}` : ""}${
                purchasableVariant?.purchasableVariantName ? ` ${purchasableVariant.purchasableVariantName}` : ""
              }`}
              isLoaded={isLoaded}
              onLoad={setLoadedCb}
              onError={setHadErrorCb}
              {...(direction === "horizontal"
                ? { width: WIDTH_SIZES[size || "medium"] }
                : { height: WIDTH_SIZES[size || "medium"] })}
              disabled={disabled ?? false}
              loading={lazyLoading ? "lazy" : "eager"}
            />
          ) : (
            <FallbackImage size={size} />
          )}
        </StyledImageContainer>
        {quantity! > 1 || quantity! < 1 ? (
          <QtyBlip
            sx={{
              ...(size === "small" && {
                top: "-7px !important",
                left: "-5px !important",
              }),
              height: "24px",
              width: "24px",
            }}
            quantity={quantity!}
          />
        ) : null}
      </Box>
    </StyledImageWrapper>
  );
}
